/**
 * Shared helper for starting an impersonation session.
 *
 * Used by:
 *   - src/routes/admin/impersonate.tsx (dedicated page)
 *   - src/client/admin/actions.ts (QuickAdminActionsDock impersonate action)
 *
 * Calls POST /api/admin/impersonation/start, parses the response,
 * writes to the auth store, and redirects via window.location.href.
 */

import { z } from 'zod'
import { useAuthStore, ImpersonationTargetSchema } from '~/client/common/stores/auth'
import { UserSchema } from '~/shared/user/user.supabase.schema'
import { supabaseClient } from '~/shared/common/supabase/client'
import { toast } from 'sonner'

const StartResponseSchema = z.object({
  token: z.string(),
  target_user: ImpersonationTargetSchema,
  // Full UserDetail for view-as rendering; optional for backward compatibility.
  target_user_detail: UserSchema.optional(),
  expires_at: z.string(),
})

/**
 * Start impersonating a user by email, Supabase UUID, or numeric identity_user id.
 *
 * On success: writes to the auth store and triggers a full-page reload to /
 * so all cached data re-fetches via X-Impersonate-Token.
 *
 * On failure: throws an Error with a human-readable message.
 */
export async function startImpersonationHelper(emailOrId: string): Promise<void> {
  const { data: { session }, error: sessionError } = await supabaseClient.auth.getSession()
  if (sessionError || !session) {
    throw new Error('You must be logged in to impersonate users.')
  }

  const response = await fetch('/api/admin/impersonation/start', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ emailOrId: emailOrId.trim() }),
  })

  const json: unknown = await response.json().catch(() => ({}))

  if (!response.ok) {
    const errorBody =
      json !== null && typeof json === 'object' && 'error' in json
        ? String((json as Record<string, unknown>).error)
        : `HTTP ${response.status}`
    throw new Error(errorBody)
  }

  const parsed = StartResponseSchema.safeParse(json)
  if (!parsed.success) {
    throw new Error('Unexpected response from server.')
  }

  const { token, target_user, target_user_detail, expires_at } = parsed.data

  useAuthStore.getState().startImpersonation({ token, target_user, target_user_detail, expires_at })

  toast.success(
    `Now impersonating ${target_user.first_name} ${target_user.last_name}`,
    {
      position: 'bottom-right',
      richColors: true,
      description: 'Your admin session is preserved. Click Stop in the banner to return to your normal admin view.',
    },
  )

  // Full-page reload so all cached data re-fetches via X-Impersonate-Token.
  window.location.href = '/'
}
